import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useProfileSx from '../../hooks/useProfileSx.js';
import {
  fieldGrantMap,
  removeFieldGrant,
  removeFieldGrantsForCampaign,
  upsertFieldGrant,
} from '../../utils/businessFunctionsPermissionUtils.js';
import { clearRecordActionsForCampaign } from '../../utils/recordPermissionUtils.js';
import RecordPermissionsPanel from './RecordPermissionsPanel.jsx';

function grantMap(items = []) {
  const map = {};
  items.forEach((item) => {
    if (!item?.id) return;
    map[item.id] = {
      can_view: Boolean(item.can_view),
      can_edit: Boolean(item.can_edit),
    };
  });
  return map;
}

function upsertGrant(list, id, patch) {
  const next = [...(list || [])];
  const index = next.findIndex((item) => item.id === id);
  const current = index >= 0 ? next[index] : { id, can_view: false, can_edit: false };
  const updated = {
    id,
    can_view: patch.can_view !== undefined ? Boolean(patch.can_view) : Boolean(current.can_view),
    can_edit: patch.can_edit !== undefined ? Boolean(patch.can_edit) : Boolean(current.can_edit),
  };

  if (!updated.can_view && !updated.can_edit) {
    return next.filter((item) => item.id !== id);
  }

  if (index >= 0) {
    next[index] = updated;
  } else {
    next.push(updated);
  }
  return next;
}

function ScopeRow({
  label,
  meta,
  depth = 0,
  expandable = false,
  expanded = false,
  onToggleExpand,
  canView,
  canEdit,
  disabled,
  viewDisabled = false,
  editDisabled = false,
  onViewChange,
  onEditChange,
  styles,
}) {
  return (
    <Box
      sx={{
        ...styles.permissionMatrixRow,
        gridTemplateColumns: 'minmax(220px, 1fr) 72px 72px',
        pl: 2 + depth * 2,
      }}
    >
      <Box sx={styles.permissionMatrixRowLabel}>
        {expandable ? (
          <IconButton size="small" onClick={onToggleExpand} disabled={disabled} sx={{ p: 0.25 }}>
            {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28 }} />
        )}
        <Box>
          <Typography sx={styles.permissionMatrixRowTitle}>{label}</Typography>
          {meta ? (
            <Typography variant="caption" sx={styles.permissionMatrixRowMeta}>
              {meta}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Box sx={styles.permissionMatrixCell}>
        <Checkbox
          size="small"
          checked={canView}
          disabled={disabled || viewDisabled}
          onChange={(event) => onViewChange(event.target.checked)}
          sx={styles.permissionMatrixCheckbox}
        />
      </Box>
      <Box sx={styles.permissionMatrixCell}>
        <Checkbox
          size="small"
          checked={canEdit}
          disabled={disabled || editDisabled}
          onChange={(event) => onEditChange(event.target.checked)}
          sx={styles.permissionMatrixCheckbox}
        />
      </Box>
    </Box>
  );
}

export default function BusinessFunctionsScopeTree({
  tree,
  resources,
  grants,
  isLoading,
  disabled,
  onChange,
  onGrantsChange,
}) {
  const styles = useProfileSx();
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedForms, setExpandedForms] = useState({});

  const accountGrants = useMemo(() => grantMap(resources?.accounts), [resources?.accounts]);
  const campaignGrants = useMemo(() => grantMap(resources?.campaigns), [resources?.campaigns]);
  const formFieldGrants = useMemo(
    () => fieldGrantMap(resources?.form_fields),
    [resources?.form_fields]
  );
  const accounts = tree?.accounts || [];

  useEffect(() => {
    if (!accounts.length) {
      setExpandedAccounts({});
      return;
    }

    setExpandedAccounts((prev) => {
      const next = { ...prev };
      accounts.forEach((account) => {
        if (account?.id && next[account.id] === undefined) {
          next[account.id] = true;
        }
      });
      return next;
    });
  }, [accounts]);

  const emitChange = (patch) => {
    onChange({
      accounts: patch.accounts ?? resources?.accounts ?? [],
      campaigns: patch.campaigns ?? resources?.campaigns ?? [],
      form_fields: patch.form_fields ?? resources?.form_fields ?? [],
    });
  };

  const handleAccountChange = (accountId, patch) => {
    emitChange({
      accounts: upsertGrant(resources?.accounts, accountId, patch),
      campaigns: resources?.campaigns || [],
      form_fields: resources?.form_fields || [],
    });
  };

  const handleCampaignChange = (campaignId, patch) => {
    const campaigns = upsertGrant(resources?.campaigns, campaignId, patch);
    let form_fields = resources?.form_fields || [];
    if (!patch.can_view && !patch.can_edit) {
      form_fields = removeFieldGrantsForCampaign(form_fields, campaignId);
    }
    emitChange({
      accounts: resources?.accounts || [],
      campaigns,
      form_fields,
    });

    if (!patch.can_view && !patch.can_edit && grants && onGrantsChange) {
      onGrantsChange(clearRecordActionsForCampaign(grants, campaignId));
    }
  };

  const handleFieldChange = (entry, patch) => {
    emitChange({
      accounts: resources?.accounts || [],
      campaigns: resources?.campaigns || [],
      form_fields: upsertFieldGrant(resources?.form_fields, entry, patch),
    });
  };

  const handleFieldInherit = (entry) => {
    emitChange({
      accounts: resources?.accounts || [],
      campaigns: resources?.campaigns || [],
      form_fields: removeFieldGrant(resources?.form_fields, entry),
    });
  };

  return (
    <Box sx={styles.permissionModuleGroup}>
      <Box
        sx={{
          ...styles.permissionMatrixHeaderRow,
          gridTemplateColumns: 'minmax(220px, 1fr) 72px 72px',
        }}
      >
        <Typography sx={styles.permissionMatrixColumnHeader}>Name</Typography>
        <Typography sx={styles.permissionMatrixColumnHeader}>View</Typography>
        <Typography sx={styles.permissionMatrixColumnHeader}>Edit</Typography>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading business functions...</Typography>
        </Box>
      ) : null}

      {!isLoading
        ? accounts.map((account) => {
            const campaigns = account.campaigns || [];
            const expanded = expandedAccounts[account.id] !== false;
            const accountGrant = accountGrants[account.id] || {};

            return (
              <Box key={account.id}>
                <ScopeRow
                  label={account.name || account.id}
                  meta="Business Function"
                  expandable={campaigns.length > 0}
                  expanded={expanded}
                  onToggleExpand={() =>
                    setExpandedAccounts((prev) => ({
                      ...prev,
                      [account.id]: !expanded,
                    }))
                  }
                  canView={Boolean(accountGrant.can_view || accountGrant.can_edit)}
                  canEdit={Boolean(accountGrant.can_edit)}
                  disabled={disabled}
                  onViewChange={(checked) =>
                    handleAccountChange(account.id, {
                      can_view: checked,
                      can_edit: checked ? accountGrant.can_edit : false,
                    })
                  }
                  onEditChange={(checked) =>
                    handleAccountChange(account.id, {
                      can_view: checked || accountGrant.can_view,
                      can_edit: checked,
                    })
                  }
                  styles={styles}
                />
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  {campaigns.map((campaign) => {
                    const campaignGrant = campaignGrants[campaign.id] || {};
                    const accountView = Boolean(accountGrant.can_view || accountGrant.can_edit);
                    const accountEdit = Boolean(accountGrant.can_edit);
                    const viewChecked = Boolean(
                      campaignGrant.can_view || campaignGrant.can_edit || accountView
                    );
                    const editChecked = Boolean(campaignGrant.can_edit || accountEdit);
                    const forms = campaign.forms || [];
                    const campaignExpanded = expandedCampaigns[campaign.id] !== false;

                    return (
                      <Box key={campaign.id}>
                        <ScopeRow
                          label={campaign.name || campaign.id}
                          meta="Business Service"
                          depth={1}
                          expandable={forms.length > 0}
                          expanded={campaignExpanded}
                          onToggleExpand={() =>
                            setExpandedCampaigns((prev) => ({
                              ...prev,
                              [campaign.id]: !campaignExpanded,
                            }))
                          }
                          canView={viewChecked}
                          canEdit={editChecked}
                          disabled={disabled}
                          viewDisabled={accountView}
                          editDisabled={accountEdit}
                          onViewChange={(checked) => {
                            if (accountView) return;
                            handleCampaignChange(campaign.id, {
                              can_view: checked,
                              can_edit: checked ? campaignGrant.can_edit : false,
                            });
                          }}
                          onEditChange={(checked) => {
                            if (accountEdit) return;
                            handleCampaignChange(campaign.id, {
                              can_view: checked || campaignGrant.can_view || accountView,
                              can_edit: checked,
                            });
                          }}
                          styles={styles}
                        />
                        <Collapse in={campaignExpanded && viewChecked} timeout="auto" unmountOnExit>
                          {grants && onGrantsChange ? (
                            <RecordPermissionsPanel
                              campaignId={campaign.id}
                              campaignName={campaign.name || campaign.id}
                              accountId={account.id}
                              grants={grants}
                              disabled={disabled}
                              serviceViewEnabled={viewChecked}
                              onGrantsChange={onGrantsChange}
                            />
                          ) : null}
                          {forms.map((form) => {
                            const formKey = `${campaign.id}:${form.id}`;
                            const formExpanded = expandedForms[formKey] !== false;
                            const fields = form.fields || [];

                            return (
                              <Box key={form.id}>
                                <ScopeRow
                                  label={form.name || form.id}
                                  meta="Form"
                                  depth={2}
                                  expandable={fields.length > 0}
                                  expanded={formExpanded}
                                  onToggleExpand={() =>
                                    setExpandedForms((prev) => ({
                                      ...prev,
                                      [formKey]: !formExpanded,
                                    }))
                                  }
                                  canView={viewChecked}
                                  canEdit={editChecked}
                                  disabled
                                  viewDisabled
                                  editDisabled
                                  onViewChange={() => {}}
                                  onEditChange={() => {}}
                                  styles={styles}
                                />
                                <Collapse in={formExpanded} timeout="auto" unmountOnExit>
                                  {fields.map((field) => {
                                    const entry = {
                                      form_type: 'leads_form',
                                      form_id: String(form.id),
                                      campaign_id: String(campaign.id),
                                      field_key: field.key,
                                    };
                                    const grantKey = `${entry.form_type}:${entry.form_id}:${entry.field_key}`;
                                    const explicitGrant = formFieldGrants[grantKey];
                                    const hasExplicitGrant = (resources?.form_fields || []).some(
                                      (item) =>
                                        (item.form_type || 'leads_form') === entry.form_type &&
                                        String(item.form_id) === entry.form_id &&
                                        item.field_key === entry.field_key
                                    );
                                    const fieldView = hasExplicitGrant
                                      ? Boolean(explicitGrant?.can_view || explicitGrant?.can_edit)
                                      : viewChecked;
                                    const fieldEdit = hasExplicitGrant
                                      ? Boolean(explicitGrant?.can_edit)
                                      : editChecked;

                                    return (
                                      <ScopeRow
                                        key={field.key}
                                        label={field.label || field.key}
                                        meta="Field"
                                        depth={3}
                                        canView={fieldView}
                                        canEdit={fieldEdit}
                                        disabled={disabled || !viewChecked}
                                        viewDisabled={false}
                                        editDisabled={editChecked}
                                        onViewChange={(checked) => {
                                          if (!checked) {
                                            handleFieldChange(entry, {
                                              can_view: false,
                                              can_edit: false,
                                            });
                                            return;
                                          }
                                          if (editChecked) {
                                            handleFieldInherit(entry);
                                            return;
                                          }
                                          handleFieldChange(entry, {
                                            can_view: true,
                                            can_edit: false,
                                          });
                                        }}
                                        onEditChange={(checked) => {
                                          if (editChecked) return;
                                          handleFieldChange(entry, {
                                            can_view: checked || fieldView,
                                            can_edit: checked,
                                          });
                                        }}
                                        styles={styles}
                                      />
                                    );
                                  })}
                                </Collapse>
                              </Box>
                            );
                          })}
                        </Collapse>
                      </Box>
                    );
                  })}
                </Collapse>
              </Box>
            );
          })
        : null}
    </Box>
  );
}
